/**
 * NotificationTrackingPanel — per-institution status, deadlines, proof notes,
 * planner import, bulk status updates, and timeline integration.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import type {
  NotificationPreset,
  NotificationStatus,
  NotificationTrackingPayload,
  TrackedNotification,
} from '../../utils/notificationTrackingTypes';
import {
  NOTIFICATION_TRACKING_VERSION,
  defaultInstitutionsForPreset,
  withIds,
} from '../../utils/notificationTrackingTypes';
import TimelineTracker from './TimelineTracker';
import { Mail, Send, Trash2 } from 'lucide-react';

const STATUS_OPTIONS: { value: NotificationStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'completed', label: 'Completed' },
];

  const TYPE_OPTIONS = [
    'Government Agency',
    'Financial Institution',
    'Employer / Business',
    'Service / Subscription',
    // no 'Other' option — type must be selected from above
  ];

export type PlannerImportKind = 'contacts' | 'cashAccounts' | 'lifeInsurance' | 'debts';

export interface PlannerImportAction {
  frameId: string;
  label: string;
  kind: PlannerImportKind;
}

export interface NotificationTrackingPanelProps {
  /** Settlement API frame id (e.g. sframe-181) */
  storageFrameId: string;
  preset: NotificationPreset;
  /** Optional: show read-only planner contacts summary (e.g. frame-146) */
  plannerSummaryFrameId?: string;
  importActions?: PlannerImportAction[];
  className?: string;
}

function emptyPayload(preset: NotificationPreset): NotificationTrackingPayload {
  return {
    version: NOTIFICATION_TRACKING_VERSION,
    institutions: withIds(defaultInstitutionsForPreset(preset)),
    calendarFocusDate: undefined,
  };
}

function normalizePayload(raw: unknown, preset: NotificationPreset): NotificationTrackingPayload {
  if (!raw || typeof raw !== 'object') return emptyPayload(preset);
  const o = raw as Record<string, unknown>;
  const list = o.institutions;
  if (!Array.isArray(list) || list.length === 0) return emptyPayload(preset);
  const institutions: TrackedNotification[] = list
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    .map((x) => ({
      id: String(x.id || crypto.randomUUID()),
      name: String(x.name || 'Unnamed'),
      category: x.category ? String(x.category) : undefined,
      status: (['pending', 'sent', 'acknowledged', 'completed'].includes(String(x.status))
        ? x.status
        : 'pending') as NotificationStatus,
      deadlineDate: x.deadlineDate ? String(x.deadlineDate) : undefined,
      reminderDaysBefore: typeof x.reminderDaysBefore === 'number' ? x.reminderDaysBefore : 7,
      notes: x.notes ? String(x.notes) : x.proofNotes ? String(x.proofNotes) : undefined,
      guidanceText: x.guidanceText ? String(x.guidanceText) : undefined,
      plannerRef: x.plannerRef ? String(x.plannerRef) : undefined,
    }));
  return {
    version: NOTIFICATION_TRACKING_VERSION,
    institutions,
    calendarFocusDate: o.calendarFocusDate ? String(o.calendarFocusDate) : undefined,
  };
}

export default function NotificationTrackingPanel({
  storageFrameId,
  preset,
  plannerSummaryFrameId: _plannerSummaryFrameId,
  importActions: _importActions,
  className = '',
}: NotificationTrackingPanelProps) {
  const { estateId: estateIdParam } = useParams<{ estateId: string }>();
  const estateId = estateIdParam || null;
  const { loadFrameData, saveFrameData, isLoading, error } = useSettlementSession(estateId);

  const [payload, setPayload] = useState<NotificationTrackingPayload>(() => emptyPayload(preset));
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const frameBaseRef = useRef<Record<string, unknown>>({});

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistFull = useCallback(
    async (full: Record<string, unknown>) => {
      if (!estateId) return;
      await saveFrameData(storageFrameId, full);
      // Notify other components that tracker has been updated
      window.dispatchEvent(new CustomEvent('notificationTracking:updated'));
    },
    [estateId, saveFrameData, storageFrameId]
  );

  useEffect(() => {
    if (!estateId) return;
    let cancelled = false;
    (async () => {
      const frame = await loadFrameData(storageFrameId);
      const base =
        frame?.data && typeof frame.data === 'object' && !Array.isArray(frame.data)
          ? { ...(frame.data as Record<string, unknown>) }
          : {};
      frameBaseRef.current = base;
      const raw = base.notificationTracking;
      if (!cancelled) setPayload(normalizePayload(raw, preset));
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData, storageFrameId, preset]);

  const scheduleSave = useCallback(
    (next: NotificationTrackingPayload) => {
      setPayload(next);
      if (!estateId) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const merged = { ...frameBaseRef.current, notificationTracking: next };
        frameBaseRef.current = merged;
        void persistFull(merged);
      }, 700);
    },
    [estateId, persistFull]
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    []
  );

  const updateRow = (id: string, patch: Partial<TrackedNotification>) => {
    scheduleSave({
      ...payload,
      institutions: payload.institutions.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });
  };

  const addRow = () => {
    // Open modal to create new row with required Type
    if (!estateId) return; // Prevent adding row without estate context
    setNewRowModal({
      category: '',
      name: '',
      status: 'pending',
      deadlineDate: undefined,
      notes: '',
    });
  };

  // New-row modal state and handlers
  const [newRowModal, setNewRowModal] = useState<{
    category: string;
    name: string;
    status: NotificationStatus;
    deadlineDate?: string;
    notes?: string;
  } | null>(null);

  const closeNewRowModal = () => setNewRowModal(null);

  const saveNewRow = () => {
    if (!newRowModal) return;
    if (!estateId || !(newRowModal.category && newRowModal.name)) return;
    const row = {
      id: crypto.randomUUID(),
      name: newRowModal.name,
      category: newRowModal.category,
      status: newRowModal.status,
      deadlineDate: newRowModal.deadlineDate,
      reminderDaysBefore: 7,
      notes: newRowModal.notes,
    } as TrackedNotification;
    scheduleSave({ ...payload, institutions: [...payload.institutions, row] });
    setNewRowModal(null);
  };

  const removeRow = (id: string) => {
    scheduleSave({
      ...payload,
      institutions: payload.institutions.filter((r) => r.id !== id),
    });
    setSelected((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAllPending = () => {
    const pending = payload.institutions.filter((i) => i.status === 'pending').map((i) => i.id);
    setSelected(new Set(pending));
  };

  const bulkMarkSent = () => {
    if (selected.size === 0) return;
    scheduleSave({
      ...payload,
      institutions: payload.institutions.map((r) =>
        selected.has(r.id) ? { ...r, status: 'sent' as const } : r
      ),
    });
    setSelected(new Set());
  };


  const focusIso = useMemo(() => {
    if (payload.calendarFocusDate) return payload.calendarFocusDate;
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-01`;
  }, [payload.calendarFocusDate]);

  const noEstate = !estateId;

  return (
    <div className={className}>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 mb-6">{error}</div>
      )}

      {noEstate && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 mb-6">
          Open this step from an estate context (URL includes estate id) to save notification tracking and load planner
          data.
        </div>
      )}

      {/* Calendar + Table grouped together with minimal spacing */}
      <div className="space-y-2">
        <TimelineTracker
          institutions={payload.institutions}
          focusDate={focusIso}
          onFocusDateChange={(iso) => scheduleSave({ ...payload, calendarFocusDate: iso })}
        />

        <p className="text-xs text-slate-500 px-1">Deadlines added below will appear here.</p>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800 flex items-center gap-2">
              <Send className="w-4 h-4 text-teal-600" aria-hidden />
              Notification status by institution
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Track pending through completed, proof of notice, and deadlines. Use bulk actions for batches of similar
              mailings.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50"
              onClick={selectAllPending}
            >
              Select all pending
            </button>
            <button
              type="button"
              className="rounded-lg bg-teal-700 px-3 py-1.5 text-sm text-white hover:bg-teal-800 disabled:opacity-40"
              onClick={bulkMarkSent}
              disabled={selected.size === 0}
            >
              Mark selected sent
            </button>
            <button
              type="button"
              className="rounded-lg border border-teal-600 text-teal-800 px-3 py-1.5 text-sm hover:bg-teal-50"
              onClick={() => addRow()}
            >
              Add row
            </button>
          </div>
        </div>

        {/* New row modal */}
        {newRowModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Add institution</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Type</label>
                  <select
                    className="w-full rounded border border-slate-200 px-2 py-1 text-sm"
                    value={newRowModal.category}
                    onChange={(e) => setNewRowModal({ ...newRowModal, category: e.target.value })}
                  >
                    <option value="" disabled>Select type</option>
                    {TYPE_OPTIONS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Institution / Party Name</label>
                  <input className="w-full rounded border border-slate-200 px-2 py-1 text-sm" value={newRowModal.name} onChange={(e) => setNewRowModal({ ...newRowModal, name: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Status</label>
                  <select className="w-full rounded border border-slate-200 px-2 py-1 text-sm" value={newRowModal.status} onChange={(e) => setNewRowModal({ ...newRowModal, status: e.target.value as NotificationStatus })}>
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Deadline</label>
                  <input type="date" className="w-full rounded border border-slate-200 px-2 py-1 text-sm" value={newRowModal.deadlineDate || ''} onChange={(e) => setNewRowModal({ ...newRowModal, deadlineDate: e.target.value || undefined })} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1 uppercase">Notes</label>
                  <textarea className="w-full rounded border border-slate-200 px-2 py-1 text-sm min-h-[70px]" value={newRowModal.notes || ''} onChange={(e) => setNewRowModal({ ...newRowModal, notes: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={closeNewRowModal} className="flex-1 rounded border border-slate-300 px-4 py-2 text-sm">Cancel</button>
                <button disabled={!newRowModal.category || !newRowModal.name} onClick={saveNewRow} className={`flex-1 rounded px-4 py-2 text-sm font-semibold ${!newRowModal.category || !newRowModal.name ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-teal-700 text-white hover:bg-teal-800'}`}>Add</button>
              </div>
            </div>
          </div>
        )}

        {/* planner import removed per simplified table spec */}

        <div className="overflow-x-auto rounded-lg border border-slate-100">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-600">
              <tr>
                <th className="px-2 py-2 w-10" aria-label="select" />
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Institution / party</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Deadline</th>
                <th className="px-3 py-2 min-w-[12rem]">Notes</th>
                <th className="px-2 py-2 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payload.institutions.map((row) => (
                <tr key={row.id} className="align-top bg-white hover:bg-slate-50/60">
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggleSelect(row.id)}
                      aria-label={`Select ${row.name}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="w-full rounded border border-slate-200 px-2 py-1 text-sm"
                      value={row.category || ''}
                      aria-required
                      onChange={(e) => updateRow(row.id, { category: e.target.value || undefined })}
                    >
                      <option value="" disabled>
                        Select type
                      </option>
                      {TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className="w-full rounded border border-slate-200 px-2 py-1 text-sm"
                      value={row.name}
                      onChange={(e) => updateRow(row.id, { name: e.target.value })}
                    />
                    {row.guidanceText && (
                      <p className="text-xs text-slate-500 mt-1 leading-snug">{row.guidanceText}</p>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="rounded border border-slate-200 px-2 py-1 text-sm w-full max-w-[11rem]"
                      value={row.status}
                      onChange={(e) => updateRow(row.id, { status: e.target.value as NotificationStatus })}
                    >
                      {STATUS_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="date"
                      className="rounded border border-slate-200 px-2 py-1 text-sm w-full max-w-[11rem]"
                      value={row.deadlineDate || ''}
                      onChange={(e) => updateRow(row.id, { deadlineDate: e.target.value || undefined })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <textarea
                      className="w-full min-h-[3rem] rounded border border-slate-200 px-2 py-1 text-sm"
                      placeholder=""
                      value={row.notes || ''}
                      onChange={(e) => updateRow(row.id, { notes: e.target.value || undefined })}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      className="p-1 text-slate-400 hover:text-red-600"
                      aria-label={`Remove ${row.name}`}
                      onClick={() => removeRow(row.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isLoading && <div className="text-center text-slate-500 text-xs py-2">Syncing…</div>}
      </div>
      </div>
      {/* End of calendar + table grouping */}

      {/* Planner import removed per simplified table spec */}

      {preset === 'subscriptions' && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 flex gap-2 mt-6">
          <Mail className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" aria-hidden />
          <p>
            Group work by category (mail, subscriptions, digital, utilities). Proof of cancellation or forwarding
            belongs in each row; upload estate-wide documents in the step document area above.
          </p>
        </div>
      )}
    </div>
  );
}
